import React, { useEffect } from 'react';
import { InputNumber, Form, message } from 'antd';
import { BaseModal, Button, DatePicker } from 'components/elements';
import { useCreateZoomRoomMutation } from 'graphql/types';
import styled from 'styled-components';
import { colors } from 'settings/style';
import moment, { Moment } from 'moment';

interface MeetingModalProps {
  modalVisible?: boolean;
  setModal?: (visible: boolean) => void;
  eventId: string;
  eventStartTime?: string | null;
  eventEndTime?: string | null;
}

interface MeetingFormValues {
  duration: number;
  startTime: Moment;
}

export const uiTimeFormat = 'YYYY-MM-DD HH:mm';

const MeetingModal = ({
  modalVisible,
  setModal,
  eventId,
  eventStartTime,
  eventEndTime,
}: MeetingModalProps) => {
  const [form] = Form.useForm<MeetingFormValues>();
  const [createZoom, { loading: createZoomLoading }] = useCreateZoomRoomMutation();

  useEffect(() => {
    if (!modalVisible) {
      return;
    }
    const start = eventStartTime ? moment(eventStartTime) : moment();
    const duration =
      eventStartTime && eventEndTime
        ? Math.max(1, moment(eventEndTime).diff(moment(eventStartTime), 'minutes') + 1)
        : 30;
    form.setFieldsValue({ startTime: start, duration });
  }, [modalVisible, eventStartTime, eventEndTime, form]);

  const onFinish = async (values: MeetingFormValues) => {
    try {
      await createZoom({
        variables: {
          eventId: eventId,
          input: {
            duration: values.duration,
            startTime: values.startTime.format(uiTimeFormat),
          },
        },
      });
      setModal?.(false);
      form.resetFields();
      message.success('Zoom Meeting Room created successfully!');
    } catch (err) {
      if (err instanceof Error) {
        message.error(err?.message);
      }
    }
  };

  return (
    <BaseModal
      open={modalVisible}
      onCancel={() => setModal?.(false)}
      footer={null}
      centered
      destroyOnClose
      width={440}
      getContainer={() => document.getElementById('root') || document.body}
      maskClosable
    >
      <MeetingBody>
        <MeetingHeading>Generate Zoom Link</MeetingHeading>
        <MeetingSubheading>Set duration and start time for your Zoom room.</MeetingSubheading>
      </MeetingBody>
      <StyledMeetingForm
        layout="vertical"
        form={form}
        onFinish={(values: unknown) => onFinish(values as MeetingFormValues)}
        validateTrigger="onChange"
      >
        <Form.Item
          label="Duration"
          name="duration"
          initialValue={30}
          rules={[{ required: true, message: 'Please input the duration of the meeting!' }]}
        >
          <InputNumber
            className="auth-number-input"
            formatter={(value) => `${value}`.replace(/([^0-9.])/g, '')}
            parser={(value) => value.replace(/([^0-9.])/g, '') as any}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="startTime"
          label="Start Time"
          rules={[{ required: true, message: 'Please select the start time' }]}
        >
          <DatePicker
            className="auth-date-input"
            showTime
            format={uiTimeFormat}
            style={{ width: '100%' }}
            placeholder="Select date and time"
          />
        </Form.Item>
      </StyledMeetingForm>
      <MeetingFooter>
        <Button type="default" onClick={() => setModal?.(false)}>
          Cancel
        </Button>
        <Button type="primary" onClick={() => form.submit()} loading={createZoomLoading}>
          Generate Zoom Link
        </Button>
      </MeetingFooter>
    </BaseModal>
  );
};

export default MeetingModal;

const MeetingBody = styled.div`
  margin-bottom: 16px;
`;

const MeetingHeading = styled.h2`
  margin: 0 0 8px;
  font-family: 'Playfair Display', Georgia, 'Times New Roman', serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${colors.grey900};
  line-height: 1.2;
`;

const MeetingSubheading = styled.p`
  margin: 0;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
  font-size: 0.9375rem;
  color: #667085;
  line-height: 1.4;
`;

const StyledMeetingForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 18px;
  }

  .ant-form-item-label > label {
    color: #4b5563;
    font-weight: 600;
    font-size: 0.875rem;
    height: auto;
    line-height: 1.3;
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
  }

  .ant-form-item-label {
    padding-bottom: 6px;
  }

  .auth-number-input.ant-input-number,
  .auth-date-input.ant-picker {
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e4e7ec;
    background: #fff;

    &:hover {
      border-color: #1a2744;
    }

    &:focus,
    &.ant-input-number-focused,
    &.ant-picker-focused {
      border-color: #1a2744;
      box-shadow: 0 0 0 3px rgba(26, 39, 68, 0.18);
    }
  }

  .auth-number-input .ant-input-number-input {
    height: 40px;
    padding: 8px 12px;
    font-size: 0.9375rem;
    color: ${colors.grey900};
  }

  .auth-date-input input {
    color: ${colors.grey900};

    &::placeholder {
      color: #9ca3af;
    }
  }
`;

const MeetingFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;
